/**
 * Auth Provider
 *
 * Browser and desktop auth context for app shell sessions.
 *
 * Primary responsibilities:
 * - Resolve Supabase browser and hosted desktop sessions
 * - Recover authenticated browser tabs after transient auth drift
 * - Clear scoped workspace storage on real sign out
 *
 * Dependencies:
 * - Supabase browser client
 * - Client session recovery
 */

import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Tables } from '@/supabase/types'
import { Session, User } from '@supabase/supabase-js'
import { usePathname } from 'next/navigation'
import { useRouter } from 'nextjs-toploader/app'

import { Loading } from '@/components/ui/loading'
import { useRootMountMarker } from '@/components/utility/root-mount-marker'
import { getProfileByUserId } from '@/db/profile'
import {
  logoutFromClient,
  redirectToLoginAfterLogout
} from '@/lib/auth/client-auth-state'
import { isLoopbackHostname } from '@/lib/auth/desktop-loopback-cookies'
import {
  recoverClientSession,
  syncBrowserRealtimeAuth,
  syncBrowserSessionToServer
} from '@/lib/auth/client-session-recovery'
import {
  hydrateDesktopHostedBrowserSession,
  resetDesktopHostedBrowserSessionHydrationState
} from '@/lib/desktop-browser-session'
import { isDesktopHost, waitForDesktopElectronApi } from '@/lib/desktop-host'
import { type DesktopSessionSnapshot, loadDesktopSessionSnapshot } from '@/lib/desktop-session-access'
import { logSupabaseAuthError } from '@/lib/auth/supabase-auth-errors'
import { supabase } from '@/lib/supabase/browser-client'
import { clearAllSidebarChatSnapshots } from '@/lib/sidebar/sidebar-chat-store'
import { clearSelectedSpaceContextFromStorage } from '@/lib/spaces/selected-space-context-storage'
import { clearSelectedWorkspaceIdFromStorage } from '@/lib/workspaces/selected-workspace-storage'

const COOKIE_MAX_AGE = 60 * 60 * 24 * 90
const LOGIN_COOKIE_NAME = 'isLoggedIn'
const DESKTOP_BOOT_SNAPSHOT_TIMEOUT_MS = 10000
const SESSION_RESUME_COOLDOWN_MS = 15_000
const SESSION_KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000
const AUTH_RELOAD_ATTEMPTS_KEY = 'losi-auth-reload-attempts'
const AUTH_RELOAD_ATTEMPT_LIMIT = 2
const AUTH_RELOAD_ATTEMPT_WINDOW_MS = 5 * 60 * 1000
const AUTH_RELOAD_LOADING_RELEASE_MS = 4_000
const AUTH_INIT_LOADING_WATCHDOG_MS = 15_000
// Retries a profile load a few times before giving up, so a transient outage
// (Supabase/CORS network blip) self-heals in seconds instead of leaving the
// profile empty until the next auth state change, which can be ~1hr away.
const PROFILE_LOAD_RETRY_DELAYS_MS = [1000, 3000, 8000]
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const shouldUseSecureClientCookie = () => {
  if (typeof window === 'undefined') {
    return process.env.NODE_ENV === 'production'
  }

  return window.location.protocol === 'https:' && !isLoopbackHostname(window.location.hostname)
}

const setPersistentCookie = (name: string, value: string, maxAge: number = COOKIE_MAX_AGE) => {
  document.cookie = `${name}=${value};path=/;max-age=${maxAge};samesite=lax;${
    shouldUseSecureClientCookie() ? 'secure;' : ''
  }`
}

const clearPersistentCookie = (name: string) => {
  if (typeof document === 'undefined') {
    return
  }

  document.cookie = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

const hasLoginCookie = () => {
  if (typeof document === 'undefined') {
    return false
  }

  return document.cookie
    .split(';')
    .some((cookie) => cookie.trim() === `${LOGIN_COOKIE_NAME}=true`)
}

const clearAuthReloadAttempts = () => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.removeItem(AUTH_RELOAD_ATTEMPTS_KEY)
  } catch {
    // Ignore storage cleanup issues.
  }
}

const attemptGuardedAuthReload = (context: string) => {
  if (typeof window === 'undefined') {
    return false
  }

  let attempts = { count: 0, firstAttemptAt: Date.now() }

  try {
    const raw = window.sessionStorage.getItem(AUTH_RELOAD_ATTEMPTS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { count?: unknown; firstAttemptAt?: unknown }
      if (
        typeof parsed.count === 'number' &&
        typeof parsed.firstAttemptAt === 'number' &&
        Date.now() - parsed.firstAttemptAt <= AUTH_RELOAD_ATTEMPT_WINDOW_MS
      ) {
        attempts = { count: parsed.count, firstAttemptAt: parsed.firstAttemptAt }
      }
    }
  } catch {
    // Treat unreadable guard state as a fresh attempt window.
  }

  if (attempts.count >= AUTH_RELOAD_ATTEMPT_LIMIT) {
    console.warn(
      `Skipping auth recovery reload (${context}): reached ${AUTH_RELOAD_ATTEMPT_LIMIT} reload attempts within the last 5 minutes.`
    )
    return false
  }

  try {
    window.sessionStorage.setItem(
      AUTH_RELOAD_ATTEMPTS_KEY,
      JSON.stringify({ count: attempts.count + 1, firstAttemptAt: attempts.firstAttemptAt })
    )
  } catch {
    // Best-effort guard persistence; still allow this reload attempt.
  }

  window.location.reload()
  return true
}

const shouldRedirectToLoginOnSignOut = (pathname: string | null) => {
  if (!pathname) return false

  return (
    pathname.startsWith('/chat') ||
    pathname.startsWith('/spaces') ||
    pathname.startsWith('/items') ||
    pathname.startsWith('/news') ||
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/setup') ||
    pathname.startsWith('/studio') ||
    pathname.startsWith('/agent') ||
    pathname.startsWith('/u')
  )
}

const isDesktopHostedLosi = () => isDesktopHost()

const clearWorkspaceClientStorage = (userId?: string | null) => {
  if (typeof window === 'undefined') return

  try {
    if (userId) {
      clearSelectedWorkspaceIdFromStorage(userId)
      clearSelectedSpaceContextFromStorage({ userId })
    }

    localStorage.removeItem('selectedWorkspace')
    localStorage.removeItem('selectedWorkspaceId')
    localStorage.removeItem('selectedFolderId')

    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('chats_'))
      .forEach((key) => sessionStorage.removeItem(key))

    clearAllSidebarChatSnapshots()
  } catch {
    // Ignore storage cleanup issues during sign out.
  }
}

const clearStorageMatchingKeys = (storage?: Storage | null) => {
  if (!storage) {
    return
  }

  const keysToRemove: string[] = []

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (!key) {
      continue
    }

    const normalized = key.toLowerCase()
    if (normalized.includes('supabase') || normalized.startsWith('sb-')) {
      keysToRemove.push(key)
    }
  }

  keysToRemove.forEach((key) => storage.removeItem(key))
}

const clearCookieMatchingKeys = () => {
  if (typeof document === 'undefined') {
    return
  }

  document.cookie.split(';').forEach((cookie) => {
    const [rawName] = cookie.split('=')
    const name = rawName?.trim().toLowerCase()
    if (!name) {
      return
    }

    if (name.includes('supabase') || name.startsWith('sb-')) {
      document.cookie = `${rawName.trim()}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`
    }
  })
}

const clearDesktopBrowserAuthState = async () => {
  if (typeof window === 'undefined') {
    return
  }

  resetDesktopHostedBrowserSessionHydrationState()

  try {
    await supabase.auth.signOut({ scope: 'local' })
  } catch {
    // Local stale session cleanup is best-effort in hosted desktop mode.
  }

  clearStorageMatchingKeys(window.localStorage)
  clearStorageMatchingKeys(window.sessionStorage)
  clearCookieMatchingKeys()
}

type SupabaseAuthWithAutoRefresh = typeof supabase.auth & {
  stopAutoRefresh?: () => void
}

const getDesktopRecoveryRetryDelayMs = (state?: {
  authRecoveryPending?: boolean
  authRecoveryRetryAt?: string | null
}) => {
  if (!state?.authRecoveryPending) {
    return 0
  }

  const retryAt = state.authRecoveryRetryAt ? new Date(state.authRecoveryRetryAt).getTime() : 0
  if (!Number.isFinite(retryAt) || retryAt <= 0) {
    return 30 * 1000
  }

  return Math.max(500, retryAt - Date.now() + 250)
}

type DesktopBootSnapshot = {
  session?:
    | (DesktopSessionSnapshot & {
        profile?: Tables<'profiles'> | null
      })
    | null
  auth?: {
    isAuthenticated?: boolean
    authRecoveryPending?: boolean
    authRecoveryReason?: string | null
    authRecoveryRetryAt?: string | null
    user?: {
      id?: string | null
      email?: string | null
    } | null
  } | null
  profile?: Tables<'profiles'> | null
}

const buildDesktopSessionSnapshotFromBoot = (
  bootSnapshot?: DesktopBootSnapshot | null
): DesktopSessionSnapshot | null => {
  const bootSession =
    bootSnapshot?.session && typeof bootSnapshot.session === 'object' ? bootSnapshot.session : null
  const bootSessionUserId =
    typeof bootSession?.user?.id === 'string' ? bootSession.user.id.trim() : ''

  if (bootSession?.isAuthenticated && bootSessionUserId) {
    return {
      ...bootSession,
      isAuthenticated: true,
      authRecoveryPending: !!bootSession.authRecoveryPending,
      authRecoveryReason:
        typeof bootSession.authRecoveryReason === 'string' ? bootSession.authRecoveryReason : '',
      authRecoveryRetryAt:
        typeof bootSession.authRecoveryRetryAt === 'string'
          ? bootSession.authRecoveryRetryAt
          : null,
      user: {
        id: bootSessionUserId,
        email:
          typeof bootSession.user?.email === 'string' && bootSession.user.email.trim().length > 0
            ? bootSession.user.email.trim()
            : null
      }
    }
  }

  const userId =
    typeof bootSnapshot?.auth?.user?.id === 'string' ? bootSnapshot.auth.user.id.trim() : ''

  if (!bootSnapshot?.auth?.isAuthenticated || !userId) {
    return null
  }

  const email =
    typeof bootSnapshot?.auth?.user?.email === 'string'
      ? bootSnapshot.auth.user.email.trim()
      : ''

  return {
    isAuthenticated: true,
    authRecoveryPending: !!bootSnapshot.auth.authRecoveryPending,
    authRecoveryReason:
      typeof bootSnapshot.auth.authRecoveryReason === 'string'
        ? bootSnapshot.auth.authRecoveryReason
        : '',
    authRecoveryRetryAt:
      typeof bootSnapshot.auth.authRecoveryRetryAt === 'string'
        ? bootSnapshot.auth.authRecoveryRetryAt
        : null,
    user: {
      id: userId,
      email: email || null
    },
    sessionData: null
  }
}

const loadDesktopBootSnapshot = async (): Promise<DesktopBootSnapshot | null> => {
  if (typeof window === 'undefined' || !isDesktopHostedLosi()) {
    return null
  }

  const electronApi = await waitForDesktopElectronApi({
    timeoutMs: DESKTOP_BOOT_SNAPSHOT_TIMEOUT_MS
  })
  const loader =
    electronApi?.desktop?.boot?.load || electronApi?.getDesktopBootSnapshot || null

  if (typeof loader !== 'function') {
    return null
  }

  const bootPromise = Promise.resolve(loader()) as Promise<DesktopBootSnapshot | null>
  const timeoutPromise = new Promise<DesktopBootSnapshot | null>((resolve) => {
    window.setTimeout(() => resolve(null), DESKTOP_BOOT_SNAPSHOT_TIMEOUT_MS)
  })

  try {
    const snapshot = await Promise.race([bootPromise, timeoutPromise])
    return snapshot && typeof snapshot === 'object' ? snapshot : null
  } catch (error) {
    console.warn('Failed to load desktop boot snapshot:', error)
    return null
  }
}

const buildOptimisticDesktopSession = (
  desktopSession?: DesktopSessionSnapshot | null
): Session | null => {
  const userId = typeof desktopSession?.user?.id === 'string' ? desktopSession.user.id.trim() : ''

  if (!desktopSession?.isAuthenticated || !userId) {
    return null
  }

  const accessToken =
    typeof desktopSession?.sessionData?.accessToken === 'string'
      ? desktopSession.sessionData.accessToken.trim()
      : ''
  const refreshToken =
    typeof desktopSession?.sessionData?.refreshToken === 'string'
      ? desktopSession.sessionData.refreshToken.trim()
      : ''
  const tokenType =
    typeof desktopSession?.sessionData?.tokenType === 'string' &&
    desktopSession.sessionData.tokenType.trim().length > 0
      ? desktopSession.sessionData.tokenType.trim()
      : 'bearer'
  const expiresAt = Number(desktopSession?.sessionData?.expiresAt)
  const expiresIn = Number(desktopSession?.sessionData?.expiresIn)
  const email =
    typeof desktopSession?.user?.email === 'string' && desktopSession.user.email.trim().length > 0
      ? desktopSession.user.email.trim()
      : undefined

  return {
    access_token: accessToken,
    refresh_token: refreshToken,
    token_type: tokenType,
    expires_at: Number.isFinite(expiresAt) ? expiresAt : undefined,
    expires_in: Number.isFinite(expiresIn) ? expiresIn : undefined,
    user: {
      id: userId,
      email,
      aud: 'authenticated',
      role: 'authenticated',
      app_metadata: {},
      user_metadata: {}
    } as Session['user']
  } as Session
}

const AuthContext = createContext<{
  session: Session | null | undefined
  user: User | null | undefined
  signOut: () => void | Promise<void>
  profile: Tables<'profiles'> | null | undefined
}>({ session: null, user: null, profile: null, signOut: () => {} })

interface AuthProviderProps {
  children: React.ReactNode
  forceLogin?: boolean
}

export const AuthProvider = ({ children, forceLogin = false }: AuthProviderProps) => {
  const [user, setUser] = useState<User>()
  const [profile, setProfile] = useState<Tables<'profiles'>>()
  const [session, setSession] = useState<Session | null>()
  const [loading, setLoading] = useState(true)
  const currentResolvedUserIdRef = useRef('')
  const router = useRouter()
  const pathname = usePathname()
  const pathnameRef = useRef<string | null>(pathname)
  const routerRef = useRef(router)
  const sessionResumeRef = useRef({
    inFlight: false,
    lastHydratedAt: 0
  })
  const profileHydrationRef = useRef({
    inFlight: false,
    lastHydratedAt: 0,
    lastUserId: ''
  })
  const desktopHosted = isDesktopHostedLosi()

  useRootMountMarker('auth-provider')

  useEffect(() => {
    pathnameRef.current = pathname
  }, [pathname])

  useEffect(() => {
    routerRef.current = router
  }, [router])

  useEffect(() => {
    let disposed = false
    let desktopRecoveryRetryTimer: number | null = null

    if (desktopHosted) {
      try {
        ;(supabase.auth as SupabaseAuthWithAutoRefresh).stopAutoRefresh?.()
      } catch (error) {
        console.warn('Failed to stop hosted desktop Supabase auto refresh:', error)
      }
    }

    const loadProfile = async (userId: string) => {
      const now = Date.now()
      const hydration = profileHydrationRef.current

      if (hydration.inFlight && hydration.lastUserId === userId) {
        return
      }

      if (hydration.lastUserId === userId && now - hydration.lastHydratedAt < 2000) {
        return
      }

      profileHydrationRef.current = {
        inFlight: true,
        lastHydratedAt: now,
        lastUserId: userId
      }

      try {
        let profileData: Tables<'profiles'> | null | undefined
        let lastError: unknown

        for (let attempt = 0; attempt <= PROFILE_LOAD_RETRY_DELAYS_MS.length; attempt++) {
          if (disposed) return

          try {
            profileData = await getProfileByUserId(userId)
            lastError = undefined
            break
          } catch (error) {
            lastError = error
            const delay = PROFILE_LOAD_RETRY_DELAYS_MS[attempt]
            if (delay === undefined) break
            await sleep(delay)
          }
        }

        if (lastError) {
          throw lastError
        }

        if (!disposed) {
          setProfile(profileData || undefined)
        }
      } finally {
        if (profileHydrationRef.current.lastUserId === userId) {
          profileHydrationRef.current.inFlight = false
          profileHydrationRef.current.lastHydratedAt = Date.now()
        }
      }
    }

    const applyResolvedSession = async (
      resolvedSession: Session | null,
      options: { preserveLoginCookie?: boolean } = {}
    ) => {
      if (disposed) {
        return
      }

      currentResolvedUserIdRef.current = resolvedSession?.user?.id || ''
      setSession(resolvedSession)
      setUser(resolvedSession?.user)

      if (resolvedSession?.user) {
        clearAuthReloadAttempts()
        setPersistentCookie(LOGIN_COOKIE_NAME, 'true')
        loadProfile(resolvedSession.user.id).catch((error) => {
          console.warn('Failed to load auth profile:', error)
        })
      } else {
        if (!options.preserveLoginCookie) {
          clearPersistentCookie(LOGIN_COOKIE_NAME)
        }
        setProfile(undefined)
      }
    }

    const preserveUnresolvedBrowserAuth = () => {
      if (!disposed) {
        setLoading(false)
      }
    }

    const releaseLoadingIfAuthReloadStalls = (context: string) => {
      window.setTimeout(() => {
        if (!disposed) {
          console.warn(
            `[auth] Reload did not complete (${context}); releasing loading overlay.`
          )
          setLoading(false)
        }
      }, AUTH_RELOAD_LOADING_RELEASE_MS)
    }

    const clearDesktopRecoveryRetry = () => {
      if (desktopRecoveryRetryTimer !== null) {
        window.clearTimeout(desktopRecoveryRetryTimer)
        desktopRecoveryRetryTimer = null
      }
    }

    const scheduleDesktopRecoveryRetry = (state?: {
      authRecoveryPending?: boolean
      authRecoveryRetryAt?: string | null
    }) => {
      clearDesktopRecoveryRetry()

      if (!desktopHosted || !state?.authRecoveryPending || disposed) {
        return
      }

      const delay = getDesktopRecoveryRetryDelayMs(state)
      desktopRecoveryRetryTimer = window.setTimeout(() => {
        desktopRecoveryRetryTimer = null

        if (!disposed) {
          void setData()
        }
      }, delay)
    }

    const getDesktopSessionSnapshot = async (): Promise<DesktopSessionSnapshot | null> => {
      if (!isDesktopHostedLosi()) {
        return null
      }

      const desktopSession = await loadDesktopSessionSnapshot({
        includeTokens: true
      })

      if (!desktopSession || desktopSession.error) {
        return null
      }

      return desktopSession
    }

    const applyDesktopBootSnapshot = async () => {
      if (!desktopHosted) {
        return
      }

      const bootSnapshot = await loadDesktopBootSnapshot()
      if (!bootSnapshot || disposed) {
        return
      }

      const bootProfile =
        bootSnapshot.profile ||
        (bootSnapshot.session &&
        typeof bootSnapshot.session === 'object' &&
        'profile' in bootSnapshot.session
          ? ((bootSnapshot.session as { profile?: Tables<'profiles'> | null }).profile ?? null)
          : null)

      if (bootProfile) {
        setProfile(bootProfile)
      }

      const bootSessionSnapshot = buildDesktopSessionSnapshotFromBoot(bootSnapshot)
      const bootSession = buildOptimisticDesktopSession(bootSessionSnapshot)

      if (bootSession?.user?.id) {
        await applyResolvedSession(bootSession)
        if (!disposed) {
          setLoading(false)
        }
      }
    }

    const hydrateDesktopSession = async () => {
      const desktopSession = await getDesktopSessionSnapshot()
      const optimisticSession = buildOptimisticDesktopSession(desktopSession)
      const recoveryState = desktopSession?.authRecoveryPending ? desktopSession : null

      if (!desktopSession) {
        return {
          session: null,
          recoveryState: null
        }
      }

      if (!desktopSession.isAuthenticated) {
        await clearDesktopBrowserAuthState()
        return {
          session: null,
          recoveryState: null
        }
      }

      if (!desktopSession.sessionData?.accessToken || !desktopSession.sessionData?.refreshToken) {
        return {
          session: optimisticSession,
          recoveryState
        }
      }

      try {
        void hydrateDesktopHostedBrowserSession(desktopSession)
          .then((hydratedSession) => {
            if (disposed) {
              return
            }

            void applyResolvedSession(hydratedSession || optimisticSession)
          })
          .catch((error) => {
            console.error('Failed to hydrate hosted desktop auth session in background:', error)

            if (!disposed) {
              void applyResolvedSession(optimisticSession)
            }
          })

        return {
          session: optimisticSession,
          recoveryState
        }
      } catch (error) {
        console.error('Failed to hydrate hosted desktop auth session:', error)
        return {
          session: optimisticSession,
          recoveryState
        }
      }
    }

    const authLoadingWatchdog = window.setTimeout(() => {
      if (!disposed) {
        console.warn('[auth] Initialization exceeded watchdog; releasing loading overlay.')
        setLoading(false)
      }
    }, AUTH_INIT_LOADING_WATCHDOG_MS)

    async function setData() {
      let shouldKeepLoading = false

      try {
        let resolvedSession: Session | null = null
        let recoveryState:
          | {
              authRecoveryPending?: boolean
              authRecoveryRetryAt?: string | null
            }
          | null = null

        if (desktopHosted) {
          const desktopHydration = await hydrateDesktopSession()
          resolvedSession = desktopHydration.session
          recoveryState = desktopHydration.recoveryState
        } else {
          const hadLoginCookie = hasLoginCookie()
          const {
            data: { session: browserSession },
            error: sessionReadError
          } = await supabase.auth.getSession()

          if (sessionReadError) {
            logSupabaseAuthError('getSession', sessionReadError)
          }

          if (browserSession?.user) {
            const synced = await syncBrowserSessionToServer(browserSession)
            if (synced.session?.user) {
              resolvedSession = synced.session
            } else {
              await syncBrowserRealtimeAuth(browserSession)
              resolvedSession = browserSession
            }
          } else if (sessionReadError || hadLoginCookie) {
            const recovered = await recoverClientSession()

            if (recovered.session?.user) {
              await syncBrowserRealtimeAuth(recovered.session)
              resolvedSession = recovered.session
            } else if (recovered.serverAuthorized && typeof window !== 'undefined') {
              if (attemptGuardedAuthReload('initial session recovery')) {
                shouldKeepLoading = true
                releaseLoadingIfAuthReloadStalls('initial session recovery')
                return
              }

              preserveUnresolvedBrowserAuth()
              return
            } else {
              if (currentResolvedUserIdRef.current) {
                preserveUnresolvedBrowserAuth()
                return
              }

              resolvedSession = null
              await applyResolvedSession(null, { preserveLoginCookie: true })
              return
            }
          } else {
            resolvedSession = null
          }
        }

        if (recoveryState?.authRecoveryPending) {
          const canContinueWithResolvedDesktopSession = Boolean(
            resolvedSession?.user?.id || currentResolvedUserIdRef.current
          )

          shouldKeepLoading = !canContinueWithResolvedDesktopSession
          if (resolvedSession?.user?.id) {
            await applyResolvedSession(resolvedSession)
          }
          if (!disposed) {
            setLoading(!canContinueWithResolvedDesktopSession)
          }
          scheduleDesktopRecoveryRetry(recoveryState)
          return
        }

        clearDesktopRecoveryRetry()
        await applyResolvedSession(resolvedSession)
      } catch (error) {
        console.error('Failed to initialize auth context:', error)
        clearDesktopRecoveryRetry()

        if (!disposed && !desktopHosted && hasLoginCookie()) {
          const recovered = await recoverClientSession().catch(() => null)

          if (recovered?.session?.user) {
            await syncBrowserRealtimeAuth(recovered.session)
            await applyResolvedSession(recovered.session)
            return
          }

          if (recovered?.serverAuthorized && typeof window !== 'undefined') {
            if (attemptGuardedAuthReload('auth initialization failure recovery')) {
              shouldKeepLoading = true
              releaseLoadingIfAuthReloadStalls('auth initialization failure recovery')
              return
            }

            preserveUnresolvedBrowserAuth()
            return
          }

          if (currentResolvedUserIdRef.current) {
            preserveUnresolvedBrowserAuth()
            return
          }
        }

        if (!disposed) {
          currentResolvedUserIdRef.current = ''
          setSession(null)
          setUser(undefined)
          setProfile(undefined)
          if (!hasLoginCookie()) {
            clearPersistentCookie(LOGIN_COOKIE_NAME)
          }
        }
      } finally {
        window.clearTimeout(authLoadingWatchdog)
        if (!shouldKeepLoading && !disposed) {
          setLoading(false)
        }
      }
    }

    if (desktopHosted) {
      void applyDesktopBootSnapshot()
    }

    void setData()

    const handleBrowserAuthStateChange = async (nextSession: Session | null) => {
      clearDesktopRecoveryRetry()

      if (nextSession?.user) {
        const synced = await syncBrowserSessionToServer(nextSession)
        const resolvedSession = synced.session?.user ? synced.session : nextSession
        if (!synced.session?.user) {
          await syncBrowserRealtimeAuth(nextSession)
        }
        await applyResolvedSession(resolvedSession)
        if (!disposed) {
          setLoading(false)
        }
        return
      }

      const signedOutUserId = currentResolvedUserIdRef.current

      if (!hasLoginCookie()) {
        clearWorkspaceClientStorage(signedOutUserId || undefined)
        await applyResolvedSession(null)
        if (!disposed) {
          setLoading(false)
        }

        if (shouldRedirectToLoginOnSignOut(pathnameRef.current)) {
          routerRef.current.replace('/login')
        }
        return
      }

      const recovered = await recoverClientSession().catch(() => null)

      if (recovered?.session?.user) {
        await syncBrowserRealtimeAuth(recovered.session)
        await applyResolvedSession(recovered.session)
        if (!disposed) {
          setLoading(false)
        }
        return
      }

      if (recovered?.serverAuthorized && typeof window !== 'undefined') {
        if (attemptGuardedAuthReload('signed out state recovery')) {
          releaseLoadingIfAuthReloadStalls('signed out state recovery')
          return
        }
      }

      preserveUnresolvedBrowserAuth()
    }

    const resumeSessionIfNeeded = async () => {
      if (desktopHosted || disposed || !hasLoginCookie()) {
        return
      }

      const now = Date.now()
      if (sessionResumeRef.current.inFlight) {
        return
      }

      if (now - sessionResumeRef.current.lastHydratedAt < SESSION_RESUME_COOLDOWN_MS) {
        return
      }

      sessionResumeRef.current.inFlight = true

      try {
        const recovered = await recoverClientSession()
        sessionResumeRef.current.lastHydratedAt = Date.now()

        if (recovered.session?.user) {
          await syncBrowserRealtimeAuth(recovered.session)
          await applyResolvedSession(recovered.session)
          if (!disposed) {
            setLoading(false)
          }
          return
        }

        if (recovered.serverAuthorized && typeof window !== 'undefined') {
          if (attemptGuardedAuthReload('session resume recovery')) {
            releaseLoadingIfAuthReloadStalls('session resume recovery')
            return
          }
        }

        preserveUnresolvedBrowserAuth()
      } catch (error) {
        console.warn('Failed to resume auth session:', error)
      } finally {
        sessionResumeRef.current.inFlight = false
      }
    }

    const handleFocus = () => {
      void resumeSessionIfNeeded()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void resumeSessionIfNeeded()
      }
    }

    let keepAliveInterval: number | null = null

    if (!desktopHosted) {
      window.addEventListener('focus', handleFocus)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      keepAliveInterval = window.setInterval(() => {
        void resumeSessionIfNeeded()
      }, SESSION_KEEPALIVE_INTERVAL_MS)
    }

    let listener: { subscription: { unsubscribe: () => void } } | null = null
    try {
      const authStateRegistration = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
        if (desktopHosted) {
          if (nextSession?.user?.id) {
            clearDesktopRecoveryRetry()
            await applyResolvedSession(nextSession)
            if (!disposed) {
              setLoading(false)
            }
            return
          }

          const desktopHydration = await hydrateDesktopSession()
          if (desktopHydration.recoveryState?.authRecoveryPending) {
            const canContinueWithResolvedDesktopSession = Boolean(
              desktopHydration.session?.user?.id || currentResolvedUserIdRef.current
            )
            if (desktopHydration.session?.user?.id) {
              await applyResolvedSession(desktopHydration.session)
            }
            if (!disposed) {
              setLoading(!canContinueWithResolvedDesktopSession)
            }
            scheduleDesktopRecoveryRetry(desktopHydration.recoveryState)
            return
          }

          if (desktopHydration.session?.user?.id) {
            clearDesktopRecoveryRetry()
            await applyResolvedSession(desktopHydration.session)
            if (!disposed) {
              setLoading(false)
            }
            return
          }

          clearDesktopRecoveryRetry()
          await clearDesktopBrowserAuthState()
          await applyResolvedSession(null)
          if (!disposed) {
            setLoading(false)
          }
          return
        }

        await handleBrowserAuthStateChange(nextSession || null)
      })
      listener = authStateRegistration?.data || null
    } catch (error) {
      console.error('Failed to register Supabase auth state listener:', error)
    }

    let desktopAuthListener: (() => void) | undefined
    let desktopAuthSuccessListener: (() => void) | undefined
    if (desktopHosted) {
      try {
        const handleDesktopAuthSignal = async (payload?: DesktopAuthStateChangePayload) => {
          const event = typeof payload?.event === 'string' ? payload.event : ''

          if (event === 'signedOut') {
            clearDesktopRecoveryRetry()
            await clearDesktopBrowserAuthState()
            await applyResolvedSession(null)
            if (!disposed) {
              setLoading(false)
            }
            return
          }

          if (payload?.authRecoveryPending) {
            const hasResolvedDesktopUser = Boolean(currentResolvedUserIdRef.current)
            if (!disposed) {
              setLoading(!hasResolvedDesktopUser)
            }
            scheduleDesktopRecoveryRetry(payload)
            return
          }

          clearDesktopRecoveryRetry()
          const desktopHydration = await hydrateDesktopSession()
          if (desktopHydration.recoveryState?.authRecoveryPending) {
            const canContinueWithResolvedDesktopSession = Boolean(
              desktopHydration.session?.user?.id || currentResolvedUserIdRef.current
            )
            if (desktopHydration.session?.user?.id) {
              await applyResolvedSession(desktopHydration.session)
            }
            if (!disposed) {
              setLoading(!canContinueWithResolvedDesktopSession)
            }
            scheduleDesktopRecoveryRetry(desktopHydration.recoveryState)
            return
          }

          await applyResolvedSession(desktopHydration.session)
          if (!disposed) {
            setLoading(false)
          }
        }

        desktopAuthListener = window.electronAPI?.onAuthStateChange?.(handleDesktopAuthSignal)
        desktopAuthSuccessListener = window.electronAPI?.onAuthSuccess?.(handleDesktopAuthSignal)
      } catch (error) {
        console.error('Failed to register desktop auth state listener:', error)
      }
    }

    return () => {
      disposed = true
      window.clearTimeout(authLoadingWatchdog)
      clearDesktopRecoveryRetry()
      if (!desktopHosted) {
        window.removeEventListener('focus', handleFocus)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        if (keepAliveInterval) {
          window.clearInterval(keepAliveInterval)
        }
      }
      listener?.subscription.unsubscribe()
      if (typeof desktopAuthListener === 'function') {
        desktopAuthListener()
      }
      if (typeof desktopAuthSuccessListener === 'function') {
        desktopAuthSuccessListener()
      }
    }
  }, [desktopHosted])

  const value = {
    session,
    user,
    profile,
    signOut: async () => {
      const signedOutUserId = currentResolvedUserIdRef.current
      const hostedDesktop = isDesktopHostedLosi()

      if (
        hostedDesktop &&
        typeof window !== 'undefined' &&
        typeof window.electronAPI?.signOut === 'function'
      ) {
        const result = await Promise.resolve(window.electronAPI.signOut())
        if (result && typeof result === 'object' && 'error' in result && result.error) {
          throw new Error(String(result.error))
        }
        await clearDesktopBrowserAuthState()
      } else if (hostedDesktop) {
        await clearDesktopBrowserAuthState()
      } else {
        await logoutFromClient(supabase)
      }

      clearWorkspaceClientStorage(signedOutUserId)
      setSession(null)
      setUser(undefined)
      setProfile(undefined)

      if (hostedDesktop) {
        router.push('/')
      } else {
        redirectToLoginAfterLogout()
      }
    }
  }

  const loadingFallback = forceLogin ? (
    <Loading withMessage={true} debugLabel="loader:auth-provider" />
  ) : (
    <Loading debugLabel="loader:auth-provider" />
  )

  return (
    <AuthContext.Provider value={value}>
      {loading ? loadingFallback : children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  return useContext(AuthContext)
}
